/**
 * tube（線状PZ）の3D形状（tube-mesh.tsが作る半楕円の筒）を、自前のWebGL描画
 * （`maplibregl.CustomLayerInterface`）で描く。
 *
 * 座標の単位は ridge-line-layer.ts と同じ（x・yは正規化メルカトルを
 * `u_worldSize`倍、zは生のメートル。同ファイル冒頭の「落とし穴1・1.5」参照）。
 * ただしx・yはメッシュの原点からの差で持ち、原点ぶんの平行移動は
 * `translatedMatrix()`で行列へ倍精度のまま畳み込む（Float32の丸めで曲面が
 * ギザギザにならないように。tube-mesh.tsの`TubeMesh`参照）。
 *
 * ★ 半透明の描き方: そのまま半透明で描くと、筒の手前の面と奥の面（両端のふた・
 *   分岐の重なりも）がすべて重なって透け、場所によって濃さがまだらになる。
 *   fill-extrusionと同じく、まず深度だけを書く下塗り（色は書かない）をしてから、
 *   いちばん手前の面だけに色を塗る2パス描画にする。面の向き（巻き方向）に
 *   依存しないので、カリングの設定を誤る心配もない。
 * ★ 陰影: 筒は横から見ると輪郭が直線になり、丸みは陰影でしか伝わらない。
 *   固定の光源（北西の上空など）だと、見る向きによっては見えている面が一様に
 *   照らされて平らな板に見える（実際に確認した）。そこで「カメラから照らす」
 *   陰影にする: カメラを向いた面ほど明るく、輪郭に近いほど暗くなるので、
 *   どの向きから見ても丸みが分かる。カメラの向きは毎フレーム地図の方位・
 *   傾きから求める（透視の効果は無視した近似で十分）。
 */

import type { CustomLayerInterface, CustomRenderMethodInput, Map as MapLibreMap } from 'maplibre-gl';

import type { TubeMesh } from './tube-mesh.ts';

const VERTEX_SHADER = `
attribute vec3 a_pos;
attribute vec3 a_normal;
attribute vec4 a_color;
uniform mat4 u_matrix;
uniform float u_worldSize;
// 面からカメラへ向かう単位ベクトル（東・北・上）
uniform vec3 u_toCamera;
varying vec4 v_color;

void main() {
  gl_Position = u_matrix * vec4(a_pos.xy * u_worldSize, a_pos.z, 1.0);
  float facing = abs(dot(normalize(a_normal), u_toCamera));
  float shade = 0.3 + 0.7 * facing;
  // MapLibreの合成は乗算済みアルファ前提
  v_color = vec4(a_color.rgb * shade * a_color.a, a_color.a);
}
`;

const FRAGMENT_SHADER = `
precision mediump float;
varying vec4 v_color;
void main() {
  gl_FragColor = v_color;
}
`;

function compileShader(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('シェーダーを作成できません');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`シェーダーのコンパイルに失敗: ${log}`);
  }
  return shader;
}

const EMPTY_MESH: TubeMesh = {
  origin: [0, 0],
  positions: new Float32Array(0),
  normals: new Float32Array(0),
  colors: new Float32Array(0),
  vertexCount: 0,
};

/**
 * 列優先の4×4行列 m に、右から平行移動 (tx, ty, 0) を掛けた行列を返す。
 * 頂点はメッシュの原点からの差で持つ（tube-mesh.tsの`TubeMesh`参照）ので、
 * 原点ぶんの平行移動をここで倍精度のまま行列へ畳み込む。
 */
function translatedMatrix(m: ArrayLike<number>, tx: number, ty: number): Float32Array {
  const out = new Float32Array(16);
  for (let i = 0; i < 12; i++) out[i] = m[i];
  for (let i = 0; i < 4; i++) out[12 + i] = m[i] * tx + m[4 + i] * ty + m[12 + i];
  return out;
}

export class TubeLayer implements CustomLayerInterface {
  id: string;
  type = 'custom' as const;
  renderingMode = '3d' as const;

  private map: MapLibreMap | null = null;
  private program: WebGLProgram | null = null;
  private matrixLoc: WebGLUniformLocation | null = null;
  private worldSizeLoc: WebGLUniformLocation | null = null;
  private toCameraLoc: WebGLUniformLocation | null = null;
  private posLoc = -1;
  private normalLoc = -1;
  private colorLoc = -1;
  private posBuffer: WebGLBuffer | null = null;
  private normalBuffer: WebGLBuffer | null = null;
  private colorBuffer: WebGLBuffer | null = null;
  private mesh: TubeMesh = EMPTY_MESH;
  private dirty = false;

  constructor(id: string) {
    this.id = id;
  }

  onAdd(map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map = map;
    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!program) throw new Error('GLプログラムを作成できません');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`GLプログラムのリンクに失敗: ${gl.getProgramInfoLog(program)}`);
    }
    this.program = program;
    this.matrixLoc = gl.getUniformLocation(program, 'u_matrix');
    this.worldSizeLoc = gl.getUniformLocation(program, 'u_worldSize');
    this.toCameraLoc = gl.getUniformLocation(program, 'u_toCamera');
    this.posLoc = gl.getAttribLocation(program, 'a_pos');
    this.normalLoc = gl.getAttribLocation(program, 'a_normal');
    this.colorLoc = gl.getAttribLocation(program, 'a_color');
    this.posBuffer = gl.createBuffer();
    this.normalBuffer = gl.createBuffer();
    this.colorBuffer = gl.createBuffer();
    // スタイル差し替えで作り直されたときも、保持中のメッシュを送り直す
    this.dirty = true;
  }

  onRemove(_map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (this.program) gl.deleteProgram(this.program);
    if (this.posBuffer) gl.deleteBuffer(this.posBuffer);
    if (this.normalBuffer) gl.deleteBuffer(this.normalBuffer);
    if (this.colorBuffer) gl.deleteBuffer(this.colorBuffer);
    this.map = null;
    this.program = null;
    this.posBuffer = null;
    this.normalBuffer = null;
    this.colorBuffer = null;
  }

  /** 描くメッシュを丸ごと差し替える（空にすれば何も描かない） */
  setMesh(mesh: TubeMesh): void {
    this.mesh = mesh;
    this.dirty = true;
    this.map?.triggerRepaint();
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    if (!this.program || !this.map || this.mesh.vertexCount === 0) return;
    gl.useProgram(this.program);
    const worldSize = this.map.transform.worldSize;
    gl.uniformMatrix4fv(
      this.matrixLoc,
      false,
      translatedMatrix(options.modelViewProjectionMatrix, this.mesh.origin[0] * worldSize, this.mesh.origin[1] * worldSize),
    );
    gl.uniform1f(this.worldSizeLoc, worldSize);
    // 方位β（北から時計回り）・傾きp（0＝真上から）のとき、カメラの視線は
    // (sinβ·sinp, cosβ·sinp, −cosp)。面からカメラへはその逆向き
    const bearing = (this.map.getBearing() * Math.PI) / 180;
    const pitch = (this.map.getPitch() * Math.PI) / 180;
    gl.uniform3f(
      this.toCameraLoc,
      -Math.sin(bearing) * Math.sin(pitch),
      -Math.cos(bearing) * Math.sin(pitch),
      Math.cos(pitch),
    );

    if (this.dirty) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.mesh.positions, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.mesh.normals, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.mesh.colors, gl.STATIC_DRAW);
      this.dirty = false;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.enableVertexAttribArray(this.posLoc);
    gl.vertexAttribPointer(this.posLoc, 3, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
    gl.enableVertexAttribArray(this.normalLoc);
    gl.vertexAttribPointer(this.normalLoc, 3, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.enableVertexAttribArray(this.colorLoc);
    gl.vertexAttribPointer(this.colorLoc, 4, gl.FLOAT, false, 0, 0);

    gl.enable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);

    // 1パス目: 深度だけ書く（色は書かない）
    gl.colorMask(false, false, false, false);
    gl.depthMask(true);
    gl.depthFunc(gl.LEQUAL);
    gl.drawArrays(gl.TRIANGLES, 0, this.mesh.vertexCount);

    // 2パス目: いちばん手前の面（1パス目と同じ深度）だけに色を塗る
    gl.colorMask(true, true, true, true);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.TRIANGLES, 0, this.mesh.vertexCount);

    // MapLibreは描画後に状態を戻すが、念のため既定に戻しておく
    gl.depthMask(true);
    gl.disableVertexAttribArray(this.posLoc);
    gl.disableVertexAttribArray(this.normalLoc);
    gl.disableVertexAttribArray(this.colorLoc);
  }
}
